import { LoadingOutlined } from '@ant-design/icons';
import { Button, Table, Tag, Tooltip } from 'antd';
import { format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import {
  OFFER_CHANNEL_COUNTRY_LABELS,
  OFFER_CHANNEL_PUBLICATION_STATE,
  OFFER_MP_STATUS,
  OFFER_PUBLICATION_STATE,
  OFFER_PUBLISH_STATUS,
} from '../../../../utils/const/mpOffers';
import {
  buildOfferRows,
  defaultExpandedRowKeys,
  selectedRowKeysFor,
} from './channelRows';
import './mpOffersTable.css';

const DASH = '—';

const formatPrice = (value, currency) => {
  if (value == null) return DASH;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return DASH;
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return DASH;
  }
};

const formatDateShort = (value) => {
  if (!value) return DASH;
  try {
    return format(parseISO(value), 'dd/MM/yyyy');
  } catch {
    return DASH;
  }
};

const stockCell = (quantity) => {
  const dotColor =
    quantity === 0 ? '#ff4d4f' : quantity > 50 ? '#52c41a' : '#fa8c16';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '2px 8px',
        borderRadius: 4,
        backgroundColor: '#f5f5f5',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <span>{quantity}</span>
    </span>
  );
};

const statusTag = (statuses, value) => {
  const { color, label } = statuses[value] || {
    color: 'default',
    label: value,
  };
  return <Tag color={color}>{label}</Tag>;
};

const mpStatusCell = (status, record) => {
  const tag = status ? statusTag(OFFER_MP_STATUS, status) : <span>{DASH}</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tag}
      {record.mp_status_checked_at && (
        <span style={{ fontSize: 11, color: '#8c8c8c' }}>
          dernier connu le {formatDateShort(record.mp_status_checked_at)}
        </span>
      )}
    </div>
  );
};

const publishStatusTag = (status) => {
  if (!status) return DASH;
  return statusTag(OFFER_PUBLISH_STATUS, status);
};

// Sous-ligne d'un pays publié séparément (Allegro CZ, SK, HU) : son propre état.
const channelPublicationTag = (channel) => {
  const tag = statusTag(
    OFFER_CHANNEL_PUBLICATION_STATE,
    channel.publication_state
  );
  if (!channel.publication_error) return tag;
  return <Tooltip title={channel.publication_error}>{tag}</Tooltip>;
};

const publicationStateCell = (state, record) => {
  if (record.channel?.publication_state) {
    return channelPublicationTag(record.channel);
  }
  if (!state) return DASH;
  let tag = statusTag(OFFER_PUBLICATION_STATE, state);
  if (state === 'error' && record.last_result_message) {
    tag = <Tooltip title={record.last_result_message}>{tag}</Tooltip>;
  }
  if (record.has_pending_integration) {
    return (
      <Tooltip title="Mise à jour en cours">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <LoadingOutlined spin style={{ color: '#1677ff' }} />
          {tag}
        </span>
      </Tooltip>
    );
  }
  return tag;
};

const storeCell = (_, record) => {
  if (!record.store?.name) return DASH;
  const country = record.store.country;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {country && (
        <ReactCountryFlag
          countryCode={country}
          svg
          style={{ width: 16, height: 12 }}
          title={country}
        />
      )}
      <span>{record.store.name}</span>
    </span>
  );
};

const mpProductCell = (_, record) => record.product?.reference || DASH;

const channelCell = (_, record) => {
  if (!record.channel) {
    const count = record.channels?.length ?? 0;
    return count > 1 ? <Tag>{count} channels</Tag> : DASH;
  }
  const { code, country } = record.channel;
  const brandLabel = OFFER_CHANNEL_COUNTRY_LABELS[country];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {!brandLabel && (
        <ReactCountryFlag
          countryCode={country}
          svg
          style={{ width: 16, height: 12 }}
          title={country}
        />
      )}
      <span>
        {code} · {brandLabel || country}
      </span>
    </span>
  );
};

// Sous-ligne : prix et promo du channel ; ligne-mère : ceux de l'offre (channel pilote).
const pricesOf = (record) => record.channel ?? record;

// Un channel a sa propre devise (Allegro CZ en CZK) ; sinon celle de l'offre.
const currencyOf = (record) => record.channel?.currency || record.currency;

const priceCell = (_, record) => {
  const prices = pricesOf(record);
  const priceError = record.channel?.price_error;
  const isChannelPriceMissing = Boolean(record.channel) && prices.price == null;
  if (priceError || isChannelPriceMissing) {
    return (
      <Tooltip title={priceError || 'Aucun prix connu pour ce channel'}>
        <Tag color="red">Prix manquant</Tag>
      </Tooltip>
    );
  }
  return formatPrice(prices.price, currencyOf(record));
};

const buildColumns = (onOfferClick) => [
  {
    title: 'ID offre',
    dataIndex: 'id',
    key: 'id',
    width: 130,
    // Sous-ligne décalée vers la droite pour la rattacher visuellement à son offre.
    render: (id, record) => (
      <Button
        type="link"
        style={{ padding: 0, marginLeft: record.channel ? 16 : 0 }}
        onClick={() => onOfferClick(id)}
      >
        #{id}
      </Button>
    ),
  },
  {
    title: 'Marketplace',
    key: 'marketplace',
    width: 140,
    render: (_, record) => record.store?.marketplace?.name || DASH,
  },
  {
    title: 'Store',
    key: 'store',
    width: 180,
    render: storeCell,
  },
  {
    title: 'Channel',
    key: 'channel',
    width: 140,
    render: channelCell,
  },
  {
    title: 'Référence produit',
    key: 'product_ref',
    width: 160,
    render: mpProductCell,
  },
  {
    title: 'Produit',
    key: 'product',
    width: 250,
    ellipsis: true,
    render: (_, record) =>
      record.product?.name || record.product?.reference || DASH,
  },
  {
    title: 'Marque',
    key: 'brand',
    width: 120,
    render: (_, record) => record.product?.brand || DASH,
  },
  {
    title: 'Prix',
    dataIndex: 'price',
    key: 'price',
    width: 110,
    sorter: true,
    render: priceCell,
  },
  {
    title: 'Prix promo',
    dataIndex: 'discount_price',
    key: 'discount_price',
    width: 110,
    render: (_, record) =>
      formatPrice(pricesOf(record).discount_price, currencyOf(record)),
  },
  {
    title: 'Début promo',
    dataIndex: 'discount_start_at',
    key: 'discount_start_at',
    width: 150,
    render: (_, record) => formatDate(pricesOf(record).discount_start_at),
  },
  {
    title: 'Fin promo',
    dataIndex: 'discount_end_at',
    key: 'discount_end_at',
    width: 150,
    render: (_, record) => formatDate(pricesOf(record).discount_end_at),
  },
  {
    title: 'Stock',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 100,
    sorter: true,
    render: stockCell,
  },
  {
    title: 'Statut offre',
    dataIndex: 'mp_status',
    key: 'mp_status',
    width: 160,
    render: mpStatusCell,
  },
  {
    title: 'Statut publication',
    dataIndex: 'publish_status',
    key: 'publish_status',
    width: 140,
    render: publishStatusTag,
  },
  {
    title: 'État publication',
    dataIndex: 'publication_state',
    key: 'publication_state',
    width: 140,
    render: publicationStateCell,
  },
  {
    title: 'Dernier envoi',
    dataIndex: 'last_pushed_at',
    key: 'last_pushed_at',
    width: 150,
    sorter: true,
    render: formatDate,
  },
  {
    title: 'Prochain envoi',
    dataIndex: 'next_push_at',
    key: 'next_push_at',
    width: 150,
    render: formatDate,
  },
  {
    title: 'Dernière synchro',
    dataIndex: 'source_updated_at',
    key: 'source_updated_at',
    width: 150,
    sorter: true,
    render: formatDate,
  },
];

const MpOffersTable = ({
  mpOffers,
  count,
  isLoading,
  currentPage = 1,
  pageSize = 20,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  onSortChange = () => {},
  selectedOffers = [],
  onSelectionChange = () => {},
  onOfferClick = () => {},
  country = null,
}) => {
  const columns = useMemo(() => buildColumns(onOfferClick), [onOfferClick]);
  const rows = useMemo(
    () => buildOfferRows(mpOffers, country),
    [mpOffers, country]
  );
  const selectedRowKeys = useMemo(
    () => selectedRowKeysFor(rows, selectedOffers),
    [rows, selectedOffers]
  );

  // Nouvelle page ou nouveau filtre pays : on repart du dépliage par défaut.
  const [expansion, setExpansion] = useState({ rows: null, keys: [] });
  if (expansion.rows !== rows) {
    setExpansion({ rows, keys: defaultExpandedRowKeys(rows, country) });
  }

  const currentPageOffers = mpOffers ?? [];

  // Cocher une sous-ligne coche son offre : la publication porte sur l'offre complète.
  const handleSelect = (record, selected) => {
    const offerId = record.offerId ?? record.id;
    const offer = currentPageOffers.find((item) => item.id === offerId);
    const otherOffers = selectedOffers.filter((item) => item.id !== offerId);
    onSelectionChange(
      selected && offer ? [...otherOffers, offer] : otherOffers
    );
  };

  const handleSelectAll = (selected) => {
    const currentPageIds = currentPageOffers.map((offer) => offer.id);
    const offersFromOtherPages = selectedOffers.filter(
      (offer) => !currentPageIds.includes(offer.id)
    );
    onSelectionChange(
      selected
        ? [...offersFromOtherPages, ...currentPageOffers]
        : offersFromOtherPages
    );
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    checkStrictly: true,
    onSelect: handleSelect,
    onSelectAll: handleSelectAll,
  };

  const handleChange = (_pagination, _filters, sorter, extra) => {
    if (extra?.action !== 'sort') return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!s || !s.field || !s.order) {
      onSortChange(null);
      return;
    }
    const prefix = s.order === 'descend' ? '-' : '';
    onSortChange(`${prefix}${s.field}`);
  };

  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey="rowKey"
      size="small"
      loading={isLoading}
      rowSelection={rowSelection}
      expandable={{
        expandedRowKeys: expansion.keys,
        onExpandedRowsChange: (keys) => setExpansion({ rows, keys }),
      }}
      rowClassName={(record) => (record.channel ? 'mp-offer-channel-row' : '')}
      onChange={handleChange}
      scroll={{ x: 'max-content' }}
      pagination={{
        current: currentPage,
        pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100', '200'],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} sur ${total} offres`,
        onChange: (page, size) => {
          if (size === pageSize) onPageChange(page);
        },
        onShowSizeChange: (_, size) => {
          onPageSizeChange(size);
          onPageChange(1);
        },
      }}
    />
  );
};

export default MpOffersTable;
